import { useState, useContext } from 'react';
import _isEmpty from 'lodash/isEmpty';
import { User } from 'commons/models/marketingDashboard';
import { MerchantContext } from 'commons-ui';
import config from 'config';
import { EMPTY_ARRAY, EMPTY_OBJECT, MERCHANT_TYPES } from '../../constants';

import { removeToken, setToken, getToken, setTokenToCookies } from '../../utils';
import AjaxContext from '../ajax/ajaxContext';

const toHexString = (bytes: Uint8Array) =>
	Array.from(bytes)
		.map((b) => b.toString(16).padStart(2, '0'))
		.join('');

let cryptoKey: CryptoKey | null = null;
const getOrCreateCryptoKey = async (): Promise<CryptoKey | null> => {
	if (cryptoKey) {
		return cryptoKey;
	}
	try {
		const keyHex: string = config.ENCRYPTION_KEY;
		const keyBuffer: Uint8Array = new Uint8Array(keyHex.match(/.{1,2}/g)!.map((byte: string) => parseInt(byte, 16)));
		cryptoKey = await window.crypto.subtle.importKey('raw', keyBuffer, { name: 'AES-GCM', length: 256 }, false, ['encrypt']);
		return cryptoKey;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Failed to import crypto key:', error);
		return null;
	}
};

const encryptEmail = async (email: string): Promise<string | null> => {
	try {
		const key = await getOrCreateCryptoKey();
		if (!key) {
			return null;
		}

		const keyHex: string = config.ENCRYPTION_KEY;
		const keyBuffer: Uint8Array = new Uint8Array(keyHex.match(/.{1,2}/g)!.map((byte: string) => parseInt(byte, 16)));
		const iv: Uint8Array = keyBuffer.slice(0, 16);

		const encoder = new TextEncoder();
		const encodedEmail = encoder.encode(email);
		const encryptedArrayBuffer = await window.crypto.subtle.encrypt(
			{
				name: 'AES-GCM',
				iv,
				tagLength: 128
			},
			key,
			encodedEmail
		);
		const tagLengthBytes = 128 / 8;
		const encryptedBuffer = new Uint8Array(encryptedArrayBuffer);
		const ciphertext = encryptedBuffer.slice(0, encryptedBuffer.byteLength - tagLengthBytes);
		const authTag = encryptedBuffer.slice(encryptedBuffer.byteLength - tagLengthBytes);
		return `${toHexString(authTag)}:${toHexString(ciphertext)}`;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Encryption failed for email:', error);
		return null;
	}
};

const isAlreadyLoggedIn = () => !_isEmpty(getToken());

const getUser = () => {
	if (isAlreadyLoggedIn()) {
		const token = getToken();
		return token?.data?.user;
	}
	return EMPTY_OBJECT;
};

const EMPTY_ROLE = {
	roleActions: EMPTY_OBJECT,
	role: ''
};
const getRole = () => {
	if (isAlreadyLoggedIn()) {
		const token = getToken();
		return token?.data?.userRole;
	}
	return EMPTY_ROLE;
};

const getPermissions = () => {
	if (isAlreadyLoggedIn()) {
		const token = getToken();
		return token?.data?.userPermissions;
	}
	return EMPTY_ARRAY;
};

const useUserProvider = () => {
	const [isLoggedIn, setIsLoggedIn] = useState(isAlreadyLoggedIn());
	const [user, setUser] = useState<User | null>(getUser());
	const [aclUsers, setAclUsers] = useState<string[]>([]);
	const [isAclEncrypted, setIsAclEncrypted] = useState<boolean>(false);
	const [roleConfig, setRoleConfig] = useState<any>(getRole());
	const [permissionsConfig, setPermissionsConfig] = useState<any>(getPermissions());
	const { postData } = useContext(AjaxContext);
	const { merchantName } = useContext(MerchantContext);

	const loginUser = async (idToken: any, accessToken: any, profileObj: any, dashboardName: string) => {
		try {
			let isAclUser: boolean = merchantName !== MERCHANT_TYPES.MARKETING;
			if (merchantName === MERCHANT_TYPES.MARKETING) {
				if (isAclEncrypted && config.ENCRYPTION_KEY) {
					const encryptedEmail: string | null = await encryptEmail(profileObj.email);
					isAclUser = !!encryptedEmail && aclUsers.includes(encryptedEmail);
				} else {
					isAclUser = aclUsers.includes(profileObj.email);
				}
			}
			const endpoint: string = isAclUser ? '/auth/unauthenticated/login' : `/${dashboardName}/unauthorised/login`;
			const loginData = await postData({
				endpoint,
				body: {
					profileObj,
					idToken,
					...(isAclUser ? { email: profileObj.email, userMerchant: merchantName } : {})
				},
				...(isAclUser ? {} : { customHeaders: { accessToken } })
			});
			const { data = EMPTY_OBJECT } = loginData;
			if (data?.data?.user?.name) {
				data.data.user.userName = data.data.user.name;
				delete data.data.user.name;
			}
			setToken(data);
			setTokenToCookies(data);
			setUser(data?.data?.user);
			setRoleConfig(data?.data?.userRole);
			if (isAclUser) setPermissionsConfig(data?.data?.userPermissions);
			setIsLoggedIn(true);
			return null;
		} catch (err: any) {
			return err;
		}
	};

	const loginOutsourceUser = async (
		reqObj: { email: string; otp: number | undefined; userMerchant: string },
		dashboardName: string
	) => {
		try {
			let isAclUser: boolean = reqObj.userMerchant !== MERCHANT_TYPES.MARKETING;
			if (reqObj.userMerchant === MERCHANT_TYPES.MARKETING) {
				if (isAclEncrypted && config.ENCRYPTION_KEY) {
					const encryptedEmail: string | null = await encryptEmail(reqObj.email);
					isAclUser = !!encryptedEmail && aclUsers.includes(encryptedEmail);
				} else {
					isAclUser = aclUsers.includes(reqObj.email);
				}
			}
			const endpoint: string = isAclUser
				? '/auth/unauthenticated/outsourceLogin'
				: `/${dashboardName}/unauthorised/outsourceLogin`;
			const loginData = await postData({
				endpoint,
				body: {
					...reqObj,
					login: 1,
					...(isAclUser ? { userMerchant: merchantName } : {})
				}
			});
			const { data = EMPTY_OBJECT } = loginData;
			if (data?.data?.user?.name) {
				data.data.user.userName = data.data.user.name;
				delete data.data.user.name;
			}
			setToken(data);
			setUser(data?.data?.user);
			setRoleConfig(data?.data?.userRole);
			if (isAclUser) setPermissionsConfig(data?.data?.userPermissions);
			setIsLoggedIn(true);
			return data;
		} catch (err: any) {
			return err;
		}
	};

	const getACLUsers = async () => {
		try {
			if (!_isEmpty(aclUsers)) return aclUsers;
			const result = await postData({
				endpoint: '/auth/unauthenticated/getACLUsers',
				body: {
					userMerchant: merchantName
				},
				customHeaders: {
					Authorization: config.ADMIN_BASIC_AUTH
				}
			});
			const { data = EMPTY_OBJECT } = result;
			setAclUsers(data.aclUsers || []);
			setIsAclEncrypted(data.isEncrypt ?? data.isEncrypted ?? false);
			return data.aclUsers;
		} catch (error) {
			return error;
		}
	};

	const checkIfLoggedIn = async (dashboardName: string, userMerchant?: string) => {
		const isExternalMerchant = userMerchant === 'EXTERNAL';
		const urlSuffix = isExternalMerchant ? '/checkOutsourceLogin' : '/checkLogin';

		let isAclUser: boolean = userMerchant !== MERCHANT_TYPES.MARKETING;
		if (userMerchant === MERCHANT_TYPES.MARKETING) {
			if (!_isEmpty(aclUsers) && user?.email) {
				if (isAclEncrypted && config.ENCRYPTION_KEY) {
					const encryptedEmail: string | null = await encryptEmail(user.email);
					isAclUser = !!encryptedEmail && aclUsers.includes(encryptedEmail);
				} else {
					isAclUser = aclUsers.includes(user.email);
				}
			}
		}
		const endpoint: string = isAclUser ? `/dashboard/unauthorized${urlSuffix}` : `/${dashboardName}/unauthorised${urlSuffix}`;

		try {
			await postData({
				endpoint,
				body: { ...(userMerchant === 'ZUPEE' ? null : { userMerchant }) }
			});
			return true;
		} catch (error) {
			return false;
		}
	};

	const logoutUser = () => {
		removeToken();
		setUser(null);
		setIsLoggedIn(false);
	};

	return {
		isLoggedIn,
		loginUser,
		logoutUser,
		user,
		roleConfig,
		permissionsConfig,
		checkIfLoggedIn,
		loginOutsourceUser,
		getACLUsers,
		aclUsers
	};
};

export default useUserProvider;
